import {useCallback, useEffect, useRef, useState} from 'react';
import {Workbox} from 'workbox-window';

export type UseAppUpdate = {
  needRefresh: boolean;
  updateApp: () => void;
};

/* v8 ignore next -- @preserve */
// Can't test PWA service worker events directly in unit tests
export function useAppUpdate(): UseAppUpdate {
  const [needRefresh, setNeedRefresh] = useState(false);
  const wbRef = useRef<Workbox | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator && import.meta.env.PROD) {
      const wb = new Workbox('/sw.js');
      wbRef.current = wb;

      // Fires when a new service worker has installed and is waiting to activate
      wb.addEventListener('waiting', () => {
        setNeedRefresh(true);
      });

      // Also check existing registration for an already-waiting worker
      void navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          setNeedRefresh(true);
        }
      });

      // Periodically check for service worker updates every 15 minutes
      const updateInterval = setInterval(
        () => {
          void wb.update();
        },
        15 * 60 * 1000,
      );

      void wb.register();

      return () => {
        clearInterval(updateInterval);
      };
    }
  }, []);

  const updateApp = useCallback(() => {
    let reloaded = false;
    const reload = () => {
      if (!reloaded) {
        reloaded = true;
        window.location.reload();
      }
    };

    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', reload, {once: true});

      if (wbRef.current) {
        wbRef.current.addEventListener('controlling', reload);
        wbRef.current.messageSkipWaiting();
      }

      void navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
          if (registration?.waiting) {
            registration.waiting.postMessage({type: 'SKIP_WAITING'});
          }
        })
        .catch(() => {
          /* no-op */
        });

      // Fallback reload if controllerchange does not fire within 500ms
      setTimeout(reload, 500);
    } else {
      window.location.reload();
    }
  }, []);

  return {
    needRefresh,
    updateApp,
  };
}
